import { apiClient, type SubmissionOptions } from './api-client';

export interface TranslationSourceDatasetEntry {
  id?: string | number;
  fr?: string;
  french?: string;
  text?: string;
  local?: string;
  source?: string;
  domain?: string;
  audioUrl?: string;
}

export interface TranslationSourceDatasetResponse {
  datasetSource?: 'lang-specific' | 'default-french' | string;
  lang?: string;
  data?: TranslationSourceDatasetEntry[];
  items?: TranslationSourceDatasetEntry[];
  results?: TranslationSourceDatasetEntry[];
  total?: number;
  page?: number;
  limit?: number;
  totalPages?: number;
  shuffleSeed?: string;
  nextCursor?: number | null;
}

export interface TranslationSourceDatasetQuery {
  page?: number;
  limit?: number;
  excludeUserId?: string;
  shuffleSeed?: string;
  cursor?: number;
  datasetSource?: string;
}

/** Body of POST /transcriptions/batch. */
export type TranscriptionBatchPayload = {
  lang: string;
  phrases: { fr: string; local: string; source?: string; domain?: string }[];
  communityProfileId?: string | number;
  communityCode?: string;
};

export const transcriptionFrenchApi = {
  getRotatedFrenchPhrases: async (params: { page?: number; limit?: number }) => {
    const q = new URLSearchParams({
      page: String(params.page ?? 1),
      limit: String(params.limit ?? 10),
    }).toString();
    const { data } = await apiClient.get(`/words/rotated-fr-phrases?${q}`);
    return data;
  },

  getTranslationSourceDataset: async (
    lang: string,
    params: TranslationSourceDatasetQuery = {}
  ): Promise<TranslationSourceDatasetResponse> => {
    const q = new URLSearchParams({
      page: String(params.page ?? 1),
      limit: String(params.limit ?? 10),
      ...(params.excludeUserId ? { excludeUserId: params.excludeUserId } : {}),
      ...(params.shuffleSeed ? { shuffleSeed: params.shuffleSeed } : {}),
      ...(params.cursor !== undefined ? { cursor: String(params.cursor) } : {}),
      ...(params.datasetSource ? { datasetSource: params.datasetSource } : {}),
    }).toString();
    const url = `/transcriptions/dataset/${encodeURIComponent(lang)}?${q}`;
    const { data } = await apiClient.get<TranslationSourceDatasetResponse>(url);
    return data;
  },

  submitBatchTranscriptions: async (payload: TranscriptionBatchPayload, options: SubmissionOptions = {}) => {
    const { data } = await apiClient.post('/transcriptions/batch', payload, {
      headers: options.headers,
      skipSessionInvalidation: options.background,
    });
    return data;
  },

  submitPhraseTranslation: async (
    lang: string,
    fr: string,
    local: string,
    opts?: PhraseTranslationOptions,
    submission?: SubmissionOptions,
  ) => {
    return transcriptionFrenchApi.submitBatchTranscriptions(phraseTranslationPayload(lang, fr, local, opts), submission);
  },
};

export type PhraseTranslationOptions = {
  source?: string;
  domain?: string;
  communityProfileId?: string | number;
  communityCode?: string;
};

/** The batch body of one phrase translation (also what the offline queue keeps). */
export function phraseTranslationPayload(
  lang: string,
  fr: string,
  local: string,
  opts?: PhraseTranslationOptions,
): TranscriptionBatchPayload {
  return {
    lang,
    phrases: [{ fr, local, source: opts?.source || '', domain: opts?.domain || '' }],
    communityProfileId: opts?.communityProfileId,
    communityCode: opts?.communityCode,
  };
}
